import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Download, FileSpreadsheet } from 'lucide-react';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { BenchmarkData } from '@/services/benchmark/types';
import { calculateChipChipDiscounts, getMarketGroupNames } from '@/services/benchmark/discountCalculator';
import { formatDateByFrequency } from '@/services/benchmark/dataAggregator';

interface ChipChipOrderData {
  date: string;
  orderCount: number;
}

interface ChipChipDiscountChartProps {
  data: BenchmarkData;
  comparisonType: 'min' | 'avg';
  frequency: 'daily' | 'weekly' | 'monthly';
  orderData?: ChipChipOrderData[];
  productName?: string;
}

const CHART_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-6))',
];

const ChipChipDiscountChart: React.FC<ChipChipDiscountChartProps> = ({
  data,
  comparisonType,
  frequency,
  orderData = [],
  productName,
}) => {
  const discountData = calculateChipChipDiscounts(data, comparisonType);
  const marketGroups = getMarketGroupNames(data);
  
  // Merge discount data with order data - include ALL dates from both datasets
  // Create a comprehensive date list from both datasets
  const allDatesSet = new Set<string>();
  discountData.forEach(item => allDatesSet.add(item.date));
  orderData.forEach(item => allDatesSet.add(item.date));
  const allDates = Array.from(allDatesSet).sort();

  // Merge both datasets, ensuring all dates are included
  const chartData = allDates.map(date => {
    const discountItem = discountData.find(d => d.date === date);
    const orderItem = orderData.find(o => o.date === date);
    
    return {
      date,
      ...(discountItem || {}),
      orderCount: orderItem?.orderCount || 0
    };
  });
  
  console.log('ChipChip Chart Data:', chartData.slice(0, 3).map(d => ({ 
    date: d.date, 
    orderCount: d.orderCount,
    hasOrderCount: d.orderCount > 0
  })));
  console.log('Order Data Received:', orderData.slice(0, 3));
  const chartRef = useRef<HTMLDivElement>(null);
  
  // State to track which lines are visible
  const [visibleLines, setVisibleLines] = useState<Record<string, boolean>>(
    marketGroups.reduce((acc, group) => ({ ...acc, [group]: true }), { orderCount: true })
  );

  // Download chart as PNG
  const downloadChart = async () => {
    if (!chartRef.current) return;
    
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
      });
      
      const link = document.createElement('a');
      link.download = `chipchip-price-advantage-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error downloading chart:', error);
    }
  };

  // Export data as Excel/CSV
  const exportData = () => {
    if (!chartData.length) return;
    
    // Prepare data for export
    const exportData = chartData.map(item => {
      const row: any = {
        'Date': item.date,
        'ChipChip Sales Price (Br)': item.chipchipPrice || '',
        'ChipChip Order Count': item.orderCount || 0
      };
      
      // Add market group columns (both price and percentage)
      marketGroups.forEach(group => {
        // Add the actual market price
        const priceKey = `${group}_price`;
        const marketPrice = item[priceKey];
        if (typeof marketPrice === 'number') {
          row[`${group} Price (Br)`] = marketPrice;
        }
        
        // Add the percentage advantage
        const value = item[group];
        if (typeof value === 'number') {
          row[`Price Advantage vs ${group} (%)`] = value;
        }
      });
      
      return row;
    });
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'ChipChip Price Advantage');
    
    // Generate filename
    const filename = `chipchip-price-advantage${productName ? `-${productName.replace(/\s+/g, '-')}` : ''}-${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Write the file
    XLSX.writeFile(wb, filename);
  };

  if (!chartData.length || !marketGroups.length) {
    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>ChipChip Price Advantage{productName ? ` for ${productName}` : ''}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-64 text-center px-4">
            <p className="text-muted-foreground text-lg font-medium mb-2">
              No ChipChip Sales Data Available
            </p>
            <p className="text-muted-foreground text-sm max-w-md">
              {productName 
                ? `No ChipChip sales records found for "${productName}" in the selected date range. This product may not have been sold through ChipChip during this period.`
                : 'No ChipChip sales data found for the selected filters. Try adjusting your date range or product selection.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Toggle line visibility
  const toggleLineVisibility = (group: string) => {
    setVisibleLines(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // Filter chart config to only include visible lines
  const chartConfig = marketGroups.reduce((config, group, index) => {
    config[group] = {
      label: `vs ${group}`,
      color: CHART_COLORS[index % CHART_COLORS.length],
    };
    return config;
  }, {
    orderCount: {
      label: 'Order Count',
      color: 'hsl(var(--muted-foreground))',
    }
  } as any);

  // Custom legend component with click functionality
  const CustomLegend = ({ payload }: any) => {
    if (!payload?.length) return null;

    return (
      <div className="flex items-center justify-center gap-4 pt-3">
        {payload.map((item: any) => {
          const group = item.dataKey;
          const isVisible = visibleLines[group];
          const itemConfig = chartConfig[group];
          
          return (
            <div
              key={item.value}
              className="flex items-center gap-1.5 cursor-pointer transition-all duration-200 hover:opacity-75 select-none"
              onClick={() => {
                console.log(`Toggling ${group}: ${isVisible} -> ${!isVisible}`);
                toggleLineVisibility(group);
              }}
              title={`Click to ${isVisible ? 'hide' : 'show'} ${itemConfig?.label}`}
            >
              <div
                className={`h-3 w-3 shrink-0 rounded-sm border-2 transition-all duration-200 ${
                  isVisible ? 'border-transparent' : 'border-current'
                }`}
                style={{
                  backgroundColor: isVisible ? item.color : 'transparent',
                  borderColor: !isVisible ? item.color : 'transparent',
                }}
              />
              <span 
                className={`text-sm transition-all duration-200 ${
                  isVisible ? 'text-foreground font-medium' : 'text-muted-foreground blur-sm'
                }`}
              >
                {itemConfig?.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatPercentage = (value: number) => {
    if (value < 0) {
      return `${Math.abs(value).toFixed(1)}% cheaper`;
    } else if (value > 0) {
      return `${value.toFixed(1)}% more expensive`;
    }
    return 'Same price';
  };

  return (
    <Card className="mb-4" ref={chartRef}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-bold text-blue-700">
              ChipChip Price Advantage{productName ? ` for ${productName}` : ''} ({comparisonType === 'min' ? 'vs Minimum' : 'vs Average'} Prices)
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Percentage showing ChipChip's price advantage vs other market groups. 
              Negative values mean ChipChip is cheaper (discount), positive values mean it's more expensive.
              Dotted line shows the number of ChipChip orders (right axis).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={exportData} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Export Data
            </Button>
            <Button 
              onClick={downloadChart} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download Chart
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-80 w-full">
          <LineChart data={chartData} margin={{ top: 20, right: 50, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              yAxisId="left"
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickFormatter={(value) => `${value} orders`}
            />
            <ReferenceLine 
              yAxisId="left"
              y={0} 
              stroke="hsl(var(--muted-foreground))" 
              strokeDasharray="2 2" 
              strokeWidth={2}
            />
            <ChartTooltip 
              content={
                <ChartTooltipContent 
                  labelFormatter={(label) => `Date: ${formatDate(label)}`}
                  formatter={(value: number, name: string) => {
                    if (name === 'orderCount') {
                      return [`${value} orders`, 'ChipChip Orders'];
                    }
                    return [
                      formatPercentage(value),
                      `ChipChip ${chartConfig[name]?.label || name}`
                    ];
                  }}
                />
              }
            />
            <ChartLegend content={<CustomLegend />} />
            
            {/* Order Count Line */}
            {visibleLines.orderCount && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="orderCount"
                stroke="hsl(var(--muted-foreground))"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            )}
            
            {marketGroups.map((group, index) => {
              if (!visibleLines[group]) return null;
              
              return (
                <Line
                  key={group}
                  yAxisId="left"
                  type="monotone"
                  dataKey={group}
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  connectNulls={true}
                />
              );
            })}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default ChipChipDiscountChart;